/**
 * SLD revisions API client. Wraps /sld-api/revisions.php.
 *
 * Revisions are how the SLD editor persists diagrams back to SP.
 * Each row is a snapshot of the topology JSON, scoped to a quote
 * (project) and tagged as either auto-generated (from project data
 * via project-topology.php) or manual (user save).
 *
 * The standalone editor without a ?project=… URL doesn't touch
 * this API at all — localStorage stays the source of truth in
 * that mode.
 */
import { sldApiUrl } from './sldApiUrl';
import type { Topology } from '../types/topology';

export interface RevisionSummary {
  id: number;
  fkuserid: string;
  label: string;
  source: 'auto' | 'manual';
  /** One-line summary built server-side, e.g. "5.0 kW SMA · 18 panels". */
  summary: string;
  created_at: string;
  updated_at: string;
}

export interface RevisionFull extends Omit<RevisionSummary, 'summary'> {
  fkquoteid: number;
  topology: Topology;
}

/** GET — list all revisions for a project, newest first. */
export async function listRevisions(qid: string | number): Promise<RevisionSummary[]> {
  const res = await fetch(sldApiUrl(`revisions.php?qid=${encodeURIComponent(String(qid))}`));
  if (!res.ok) {
    throw new Error(`listRevisions failed (${res.status})`);
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/** GET — load a single revision by id. */
export async function loadRevision(id: number): Promise<RevisionFull> {
  const res = await fetch(sldApiUrl(`revisions.php?id=${id}`));
  if (!res.ok) {
    throw new Error(`loadRevision failed (${res.status})`);
  }
  return res.json();
}

/**
 * POST — create a new revision. Returns the new revision's metadata
 * with `id` populated. `source` defaults to 'manual' if omitted —
 * pass 'auto' when seeding from project data.
 */
export async function createRevision(input: {
  qid: string | number;
  topology: Topology;
  label?: string;
  source?: 'auto' | 'manual';
  fkuserid?: string;
}): Promise<{ id: number; label: string; source: 'auto' | 'manual'; created_at: string; updated_at: string }> {
  const res = await fetch(sldApiUrl('revisions.php'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      qid: input.qid,
      topology: input.topology,
      label: input.label,
      source: input.source ?? 'manual',
      fkuserid: input.fkuserid ?? '',
    }),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`createRevision failed (${res.status}): ${detail}`);
  }
  return res.json();
}

/** POST — update an existing revision in place. */
export async function updateRevision(
  id: number,
  topology: Topology,
  label?: string,
): Promise<{ id: number; updated_at: string }> {
  const res = await fetch(sldApiUrl(`revisions.php?id=${id}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ topology, label }),
  });
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`updateRevision failed (${res.status}): ${detail}`);
  }
  return res.json();
}

/** DELETE — remove a revision. */
export async function deleteRevision(id: number): Promise<{ id: number; deleted: boolean }> {
  const res = await fetch(sldApiUrl(`revisions.php?id=${id}`), { method: 'DELETE' });
  if (!res.ok) {
    throw new Error(`deleteRevision failed (${res.status})`);
  }
  return res.json();
}
